// UsersListPage.js

import React, { useCallback, useContext, useEffect, useState } from 'react';

import Banner from '../../b2b-common/Banner';
import { CircularProgress, Paper } from '@mui/material';
import { LoginContext } from '../../../App';
import { fetchAllUsersWithMembershipCount_Paged } from '../../../services/b2b-users-apiService';
import UsersTable from '../components/user/UsersTable';
import UserDetailsModal from '../forms/UserDetailsModal';
import { generateTextField } from '../../b2b-common/helpers';
import CustomButton from '../../b2b-common/CustomButton';
import { Search } from '@mui/icons-material';

import './UsersListPage.css';
import B2BFormContainer from '../../b2b-common/B2BFormContainer';

const UsersListPage = () => {
    const [usersDataWithMembershipCount, setUsersDataWithMembershipCount] = useState(null);
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pagesCount, setPagesCount] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [refresh, setRefresh] = useState(true);
    const [searchFields, setSearchFields] = useState({
        email: '',
        firstName: '',
        lastName: '',
    });

    const { loginData } = useContext(LoginContext);

    const fetchUsersData = useCallback(async (page) => {
        try {
            if (loginData) {
                setLoading(true);
                const { accessToken } = loginData;
                const data = await fetchAllUsersWithMembershipCount_Paged(page - 1, 10, searchFields, accessToken);
                setUsersDataWithMembershipCount(data);
                setPagesCount(data.totalPages);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [loginData, searchFields]);

    useEffect(() => {
        if ((refresh) && (page !== 0)) {
            fetchUsersData(page);
            setRefresh(false);
        }
    }, [page, refresh, fetchUsersData]);

    const handleSearchFieldChange = (field, value) => {
        setSearchFields((prevSearchInfo) => ({
            ...prevSearchInfo,
            [field]: value,
        }));
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleUpdateUser = (updatedUser) => {
        setRefresh(true);
        setSelectedUser(updatedUser);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        setRefresh(true);
    };

    const buttonsConfig = [
        {
            label: 'Search',
            disabledText: 'Search',
            onClick: () => {
                fetchUsersData(1);
                setPage(1);
            },
            isLoading: loading,
            color: 'secondary',
            variant: 'contained',
            key: '1',
            icon: <Search />,
        },
    ];

    return (
        <>
            <div className="users-list-page-container">
                <Banner message="Users Menu: List platform users." />
                <B2BFormContainer title="Search form" className="users-list-search-form" orientation="horizontal" showDivider={false} buttonsConfig={buttonsConfig}>
                    {generateTextField("First Name", searchFields.firstName, "users-list-search-field", false, (e) => handleSearchFieldChange('firstName', e.target.value), { required: false })}
                    {generateTextField("Last Name", searchFields.lastName, "users-list-search-field", false, (e) => handleSearchFieldChange('lastName', e.target.value), { required: false })}
                    {generateTextField("Email", searchFields.email, "users-list-search-field", false, (e) => handleSearchFieldChange('email', e.target.value), { required: false })}
                </B2BFormContainer>
                {loading && <CircularProgress className="centered-spinner" />}
                {error && <p>Error: {error}</p>}
                {!loading && usersDataWithMembershipCount && (
                    <div className="users-list-table">
                        <UsersTable
                            usersPagedData={usersDataWithMembershipCount}
                            onSelectUser={handleSelectUser}
                            pagesCount={pagesCount}
                            currentPage={page}
                            onPageChange={handlePageChange}
                            className={'users-table'}
                        />
                    </div>
                )}
                <UserDetailsModal
                    open={isModalOpen}
                    user={selectedUser}
                    onUserUpdate={handleUpdateUser}
                    onClose={() => setIsModalOpen(false)}
                />
            </div>
        </>
    );
}

export default UsersListPage;